import { ApiClient } from '../config';

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  roles: string[];
  status: 'active' | 'inactive';
  createdAt: string;
}

export interface CreateUserInput {
  email: string;
  name: string;
  password: string;
  roles: string[];
}

export interface UpdateUserInput {
  email: string;
  name?: string;
  roles: string[];
}

export interface ResetPasswordInput {
  newPassword: string;
  confirmPassword: string;
}

export interface DeletedUser {
  id: string;
  email: string;
  name: string | null;
  roles: string[];
  status: string;
  deletedAt: string;
  deletedBy: string | null;
  deletionReason: string | null;
  deletedByUser: {
    id: string;
    email: string;
    name: string | null;
  } | null;
}

export interface UserService {
  getUsers(searchTerm?: string): Promise<AdminUser[]>;
  createUser(data: CreateUserInput): Promise<AdminUser>;
  updateUser(id: string, data: UpdateUserInput): Promise<AdminUser>;
  deactivateUser(id: string): Promise<void>;
  reactivateUser(id: string): Promise<void>;
  deleteUser(id: string): Promise<void>;
  resetPassword(id: string, data: ResetPasswordInput): Promise<void>;
  getTrashedUsers(): Promise<DeletedUser[]>;
  restoreUser(id: string): Promise<void>;
  permanentlyDeleteUser(id: string): Promise<void>;
}

export class UserServiceImpl implements UserService {
  private apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  async getUsers(searchTerm?: string): Promise<AdminUser[]> {
    return this.apiClient.get<AdminUser[]>(
      '/api/v1/users',
      searchTerm ? { search: searchTerm } : undefined
    );
  }

  async createUser(data: CreateUserInput): Promise<AdminUser> {
    return this.apiClient.post<AdminUser>('/api/v1/users', data);
  }

  async updateUser(id: string, data: UpdateUserInput): Promise<AdminUser> {
    return this.apiClient.patch<AdminUser>(`/api/v1/users/${id}`, {
      email: data.email,
      name: data.name,
      roles: data.roles,
    });
  }

  async deactivateUser(id: string): Promise<void> {
    await this.apiClient.post(`/api/v1/users/${id}/deactivate`, {});
  }

  async reactivateUser(id: string): Promise<void> {
    await this.apiClient.post(`/api/v1/users/${id}/reactivate`, {});
  }

  async deleteUser(id: string): Promise<void> {
    await this.apiClient.delete(`/api/v1/users/${id}`);
  }

  async resetPassword(id: string, data: ResetPasswordInput): Promise<void> {
    await this.apiClient.post(`/api/v1/users/${id}/reset-password`, data);
  }

  async getTrashedUsers(): Promise<DeletedUser[]> {
    return this.apiClient.get<DeletedUser[]>('/api/v1/users/trash');
  }

  async restoreUser(id: string): Promise<void> {
    await this.apiClient.post(`/api/v1/users/${id}/restore`, {});
  }

  async permanentlyDeleteUser(id: string): Promise<void> {
    await this.apiClient.delete(`/api/v1/users/${id}/permanent`);
  }
}

export const userService = new UserServiceImpl(new ApiClient());

export function createUserService(apiClient: ApiClient): UserService {
  return new UserServiceImpl(apiClient);
}
